import { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { useBranding } from "../../hooks/useBranding";
import { useData } from "../../hooks/useData";
import SiteFooter from "../../components/SiteFooter";
import { B, Btn, fonts } from "../../components/UI";
import StickyCallButton from "../../components/StickyCallButton";

const PAGE_CONTENT = {
  "/kurti-manufacturer-ahmedabad": {
    title: "Kurti Manufacturer in Ahmedabad",
    metaTitle: "Kurti Manufacturer in Ahmedabad | Wholesale Kurtis Supplier",
    description:
      "Leading kurti manufacturer in Ahmedabad - Nilam Synthetics. We offer wholesale cotton kurtis, designer kurtis, 2pcs & 3pcs sets with bulk supply across India.",
    subtitle:
      "Wholesale kurti manufacturer in Ahmedabad supplying retailers, boutiques and resellers across India.",
    content: `
Nilam Synthetics is a trusted kurti manufacturer in Ahmedabad specializing in wholesale ethnic wear including cotton kurtis, designer kurtis, and coord sets. 

With a strong base in Ahmedabad's textile market, we manufacture high-quality kurtis with consistent sizing, trending designs, and bulk availability.

We cater to wholesalers, boutique owners, and online sellers looking for reliable kurti suppliers in Ahmedabad with competitive pricing and fast dispatch.`,
    chips: ["Kurti Manufacturer", "Ahmedabad Based", "Bulk Supply"],
  },

  "/wholesale-kurtis-ahmedabad": {
    title: "Wholesale Kurtis in Ahmedabad",
    metaTitle: "Wholesale Kurtis in Ahmedabad | Bulk Kurti Supplier",
    description:
      "Wholesale kurtis supplier in Ahmedabad offering cotton, rayon and designer kurtis at best prices. Bulk orders with fast delivery across India.",
    subtitle:
      "Bulk kurtis supplier in Ahmedabad for retailers, resellers and boutiques.",
    content: `
Nilam Synthetics is a leading wholesale kurtis supplier in Ahmedabad offering a wide collection of cotton kurtis, designer kurtis and festive wear.

We provide competitive wholesale pricing, ensuring high margins for retailers and resellers. Our collection is updated regularly to match current fashion trends.

With a strong manufacturing and supply chain, we ensure fast dispatch and reliable bulk order fulfillment across India.`,
    chips: ["Wholesale Kurtis", "Bulk Orders", "Pan India Supply"],
  },

  "/cotton-kurtis-manufacturer": {
    title: "Cotton Kurtis Manufacturer in Ahmedabad",
    metaTitle: "Cotton Kurtis Manufacturer | Wholesale Cotton Kurtis Ahmedabad",
    description:
      "Cotton kurtis manufacturer in Ahmedabad offering breathable, stylish and durable kurtis for wholesale buyers and retailers.",
    subtitle:
      "Comfortable and trendy cotton kurtis manufactured for bulk buyers.",
    content: `
Nilam Synthetics is a specialized cotton kurtis manufacturer in Ahmedabad offering premium quality breathable kurtis for daily wear.

Our cotton kurtis are designed for comfort, durability and style, making them ideal for retailers and bulk buyers. We offer a wide range of prints, sizes and patterns.

We ensure consistent quality and timely delivery for all wholesale orders.`,
    chips: ["Cotton Kurtis", "Daily Wear", "Bulk Manufacturing"],
  },

  "/designer-kurtis-wholesale": {
    title: "Designer Kurtis Wholesale Supplier",
    metaTitle: "Designer Kurtis Wholesale | Boutique Kurti Supplier",
    description:
      "Designer kurtis wholesale supplier in Ahmedabad offering trendy, embroidered and festive kurtis for boutiques and resellers.",
    subtitle:
      "Premium designer kurtis for boutiques and online sellers.",
    content: `
Nilam Synthetics is a designer kurtis wholesale supplier in Ahmedabad offering premium and trendy collections for boutiques and resellers.

Our range includes embroidered kurtis, festive wear and coord sets designed to match modern fashion trends.

We provide bulk supply with consistent quality and competitive wholesale pricing.`,
    chips: ["Designer Kurtis", "Boutique Collection", "Wholesale Supply"],
  },
};

export default function SeoLandingPage() {
  const { branding } = useBranding();
  const { categories } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const accent = branding.primaryColor || B.orange;

  const content = useMemo(() => PAGE_CONTENT[location.pathname] || PAGE_CONTENT["/kurti-manufacturer-ahmedabad"], [location.pathname]);
  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  return (
    <>
      <Helmet>
        <title>{content.metaTitle} | {branding.brandName}</title>

        <meta name="description" content={content.description} />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={canonicalUrl} />

        {/* Local SEO Boost */}
        <meta name="geo.region" content="IN-GJ" />
        <meta name="geo.placename" content="Ahmedabad" />

        {/* Open Graph */}
        <meta property="og:title" content={content.metaTitle} />
        <meta property="og:description" content={content.description} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <section
        style={{
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <section style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: 24 }}>
            {content.title}
          </h2>

          <p style={{ fontFamily: fonts.body, lineHeight: 1.8, color: B.textMid }}>
            {content.content}
          </p>
        </section>
        <div style={{ maxWidth: 920, margin: "0 auto" }}>
          <p
            style={{
              color: accent,
              fontFamily: fonts.body,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              fontWeight: 700,
              fontSize: 11,
              marginBottom: 14,
            }}
          >
            Nilam Synthetics · Ahmedabad
          </p>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 16,
              maxWidth: 700,
              margin: "14px auto 0",
              lineHeight: 1.75,
            }}
          >
            {content.subtitle}
          </p>

          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 10, marginTop: 26 }}>
            {content.chips.map((chip) => (
              <span
                key={chip}
                style={{
                  border: `1px solid ${accent}55`,
                  background: `${accent}1A`,
                  borderRadius: 999,
                  padding: "8px 16px",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section style={{ maxWidth: 1060, margin: "0 auto", padding: "52px 24px 16px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {[
            ["Quality Promise", "Every design is curated for finish, fit and reliable repeat ordering."],
            ["Business Friendly MOQ", "Flexible order support for new buyers and large-volume resellers."],
            ["Fast Assistance", "Connect with our team on call or WhatsApp for quick stock checks."],
          ].map(([title, text]) => (
            <div
              key={title}
              style={{
                background: B.white,
                border: `1px solid ${B.borderLight}`,
                borderRadius: 14,
                padding: "22px 20px",
                boxShadow: "0 4px 18px rgba(0,0,0,0.03)",
              }}
            >
              <h2 style={{ margin: "0 0 10px", fontFamily: fonts.display, fontSize: 18, color: B.text }}>{title}</h2>
              <p style={{ margin: 0, fontFamily: fonts.body, fontSize: 14, lineHeight: 1.7, color: B.textMid }}>{text}</p>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 30,
            background: B.orangeFaint,
            border: `1px solid ${B.orangeMid}`,
            borderRadius: 16,
            padding: "26px 20px",
            textAlign: "center",
          }}
        >
          <h2 style={{ margin: "0 0 8px", fontFamily: fonts.display, color: B.text, fontSize: 24 }}>Ready to explore the latest catalog?</h2>
          <p style={{ margin: "0 0 18px", fontFamily: fonts.body, color: B.textMid, fontSize: 14 }}>
            Browse live designs and connect directly with our team for wholesale enquiries.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn onClick={() => navigate("/")}>Browse Catalog</Btn>
            {branding.whatsapp && (
              <a
                href={`https://wa.me/${branding.whatsapp.replace(/\D/g, "")}?text=Hi! I want details for wholesale kurtis.`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "11px 20px",
                  borderRadius: 8,
                  textDecoration: "none",
                  background: "#25D366",
                  color: "#fff",
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                WhatsApp Enquiry
              </a>
            )}
          </div>
        </div>
      </section>

      <SiteFooter branding={branding} categories={categories} />
      <StickyCallButton phone={branding.phone} />
    </>
  );
}
